import React from "react";
import { cx } from "utils/join-classnames";
import {
    Button,
    Table,
    UserModal,
    LoaderContainer,
    ConfirmActionModal,
} from "ui/components";
import { useSessionContext } from "ui/context/SessionContext";
import { useNotifications } from "ui/context/NotificationsContext";
import { User } from "core/models";
import usePermissions from "ui/hooks/usePermissions";
import {
    listUsersInProject,
    excludeFromProject,
    requestPasswordResetForUser,
    requestActivationForUser,
} from "core/services/site/user";
import styles from "./Users.module.scss";

const SettingsPage: React.FC = () => {
    const userPermissions = usePermissions();
    const [addmodal, setAddmodal] = React.useState<boolean>(false);
    const [editableUser, setEditableUser] = React.useState<User>();
    const [editmodal, setEditmodal] = React.useState<boolean>(false);
    const [deleteModal, setDeleteModal] = React.useState<boolean>(false);
    const [deleteUserId, setDeleteUserId] = React.useState<number>();
    const [resetPasswordId, setResetPasswordId] = React.useState<number>();
    const [requestActivationId, setRequestActivationId] =
        React.useState<number>();
    const [deletePending, setDeletePending] = React.useState<boolean>(false);
    const [passwordPending, setPasswordPending] =
        React.useState<boolean>(false);
    const [activationPending, setActivationPending] =
        React.useState<boolean>(false);
    const sessionContext = useSessionContext();
    const { addNotification } = useNotifications();

    const userIsProjectAdmin = userPermissions.isProjectAdmin;

    const useUsers = () => {
        const [users, setUsers] = React.useState<User[]>([]);
        const [pending, setPending] = React.useState<boolean>(false);
        const getUsers = async () => {
            setPending(true);
            try {
                if (!sessionContext.session) {
                    throw Error("missing session");
                }
                if (!sessionContext.selectedProject) {
                    throw Error("missing project");
                }
                const result = await listUsersInProject({
                    accessToken: sessionContext.session.token,
                    projectId: sessionContext.selectedProject.id,
                });
                if (result.error) {
                    throw new Error("not able to fetch users");
                }
                setPending(false);
                return result.value;
            } catch (err) {
                addNotification(
                    "error",
                    "Error while fetching users",
                    (err as Error).message,
                    5000,
                );
                setPending(false);
                return undefined;
            }
        };

        const refreshUsers = React.useCallback(async () => {
            if (!userIsProjectAdmin) {
                return;
            }
            const fetchedUsers = await getUsers();
            if (fetchedUsers) {
                setUsers(fetchedUsers);
            }
        }, []);

        const deleteUser = async () => {
            try {
                if (!sessionContext.session) {
                    throw Error("missing session");
                }
                if (!sessionContext.selectedProject) {
                    throw Error("missing project");
                }
                setDeletePending(true);
                if (!deleteUserId) {
                    throw new Error("no AddonID was found");
                }

                const result = await excludeFromProject(
                    sessionContext.session.token,
                    sessionContext.selectedProject.id,
                    deleteUserId,
                );
                if (result.error) {
                    throw Error(result.error);
                }
                const newUsers = users.filter(
                    (user) => user.id !== deleteUserId,
                );
                setUsers(newUsers);

                setDeleteModal(false);
                setDeletePending(false);
            } catch (err) {
                addNotification(
                    "error",
                    "Error while deleting user",
                    (err as Error).message,
                    5000,
                );
                setDeleteModal(false);
                setDeletePending(false);
            }
        };
        React.useEffect(() => {
            refreshUsers();
        }, [refreshUsers]);

        return {
            users,
            pending,
            deleteUser,
            refreshUsers,
        };
    };

    const { users, pending, refreshUsers, deleteUser } = useUsers();

    if (!userIsProjectAdmin) {
        return (
            <div className={styles.heading}>
                <div className={styles.heading__left}>
                    <h1 className={cx(styles.heading, styles.heading__primary)}>
                        Not available
                    </h1>
                    <span className={styles.heading__desc}>
                        You don&apos;t seem to have permission
                    </span>
                </div>
            </div>
        );
    }

    const handleCloseModal = () => {
        setAddmodal(false);
        setEditmodal(false);
    };
    const handleSaveModalAdd = () => {
        refreshUsers();
        setAddmodal(false);
    };
    const handleSaveModalEdit = async () => {
        refreshUsers();
        setEditmodal(false);
    };
    const handleAddBtn = () => {
        setAddmodal(true);
    };
    const handleEdit = (user: User) => {
        setEditableUser(user);
        setEditmodal(true);
    };
    const handleRequestDelete = (userId: number) => {
        setDeleteUserId(userId);
        setDeleteModal(true);
    };
    const closeDeleteModal = () => {
        setDeleteModal(false);
        setDeleteUserId(undefined);
    };
    const handleConfirmDeleteUser = () => {
        deleteUser();
    };
    const handleResetPasswordRequest = (userId: number) => {
        setResetPasswordId(userId);
    };
    const closeResetPasswordModal = () => {
        setResetPasswordId(undefined);
    };
    const handleResetPassword = async () => {
        setPasswordPending(true);
        try {
            if (!sessionContext.session) {
                throw Error("missing session");
            }
            if (!sessionContext.selectedProject) {
                throw Error("missing project");
            }
            if (!resetPasswordId) {
                throw Error("missing resetPasswordId");
            }
            const result = await requestPasswordResetForUser(
                sessionContext.session.token,
                sessionContext.selectedProject.id,
                resetPasswordId,
            );
            if (result.error) {
                throw Error(result.error);
            }
            setResetPasswordId(undefined);
            addNotification(
                "success",
                "Password reset was send",
                "The user can check his/her email",
                5000,
            );
            setPasswordPending(false);
        } catch (err) {
            addNotification(
                "error",
                "Error requesting password reset",
                (err as Error).message,
                5000,
            );
            setPasswordPending(false);
            setResetPasswordId(undefined);
        }
    };
    const handleResendActivationRequest = (userId: number) => {
        setRequestActivationId(userId);
    };
    const closeResendActivationModal = () => {
        setRequestActivationId(undefined);
    };
    const handleResendActivation = async () => {
        try {
            if (!sessionContext.session) {
                throw Error("missing session");
            }
            if (!sessionContext.selectedProject) {
                throw Error("missing project");
            }
            if (!requestActivationId) {
                throw Error("missing requestActivationId");
            }
            const result = await requestActivationForUser(
                sessionContext.session.token,
                sessionContext.selectedProject.id,
                requestActivationId,
            );
            if (result.error) {
                throw Error(result.error);
            }
            setRequestActivationId(undefined);
            addNotification(
                "success",
                "Activation was re-sent",
                "The user can check his/her email",
                5000,
            );
            setActivationPending(false);
        } catch (err) {
            addNotification(
                "error",
                "Error resending activation",
                (err as Error).message,
                5000,
            );
            setActivationPending(false);
            setRequestActivationId(undefined);
        }
    };

    const formatDate = (date: Date): string => {
        const input = new Date(date);
        const today = new Date();
        if (input.setHours(0, 0, 0, 0) === today.setHours(0, 0, 0, 0)) {
            return "Today";
        }
        return input.toLocaleDateString("en-EN", {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    };

    return (
        <div className={styles.section}>
            <div className={styles.heading}>
                <div className={styles.heading__left}>
                    <h1 className={cx(styles.heading, styles.heading__primary)}>
                        Users &amp; Permissions
                    </h1>
                    <span className={styles.heading__desc}>
                        Manage your users and their permission settings
                    </span>
                </div>
                <div className={styles.heading__right}>
                    <Button icon="plus" type="primary" onClick={handleAddBtn}>
                        Add User
                    </Button>
                </div>
            </div>

            {pending && (
                <LoaderContainer message="Loading users..." theme="dark" />
            )}
            {!pending && users.length === 0 && (
                <div className={styles.emptymsg}>There are no users yet!</div>
            )}
            {users.length > 0 && (
                <Table.Container>
                    <Table.Head sortable>
                        <Table.Label label="Email" width="filled" />
                        <Table.Label label="Joined project on" width="filled" />
                        <Table.Label label="Last login" width="filled" />
                        <Table.Label label="" width="filled" />
                    </Table.Head>
                    <Table.Body>
                        {users.map((user) => (
                            <Table.Row key={user.id}>
                                <Table.Cell width="filled">
                                    <span
                                        className={
                                            !user.activated
                                                ? styles.inactive
                                                : undefined
                                        }
                                    >
                                        {user.email
                                            ? user.email
                                            : user.username}
                                    </span>
                                </Table.Cell>
                                <Table.Cell width="filled">
                                    <span
                                        className={
                                            !user.activated
                                                ? styles.inactive
                                                : undefined
                                        }
                                    >
                                        {formatDate(user.creationDate)}
                                    </span>
                                </Table.Cell>
                                <Table.Cell width="filled">
                                    <span
                                        className={
                                            !user.activated
                                                ? styles.inactive
                                                : undefined
                                        }
                                    >
                                        {user.trackingSession
                                            ? formatDate(
                                                  user.trackingSession
                                                      .accessedDate,
                                              )
                                            : "Has never logged in"}
                                    </span>
                                </Table.Cell>

                                <Table.Cell width="filled">
                                    {user.activated && (
                                        <span
                                            className={styles.resetpassword}
                                            onClick={() =>
                                                handleResetPasswordRequest(
                                                    user.id,
                                                )
                                            }
                                            role="presentation"
                                        >
                                            Reset password
                                        </span>
                                    )}
                                    {!user.activated && (
                                        <Button
                                            type="icon"
                                            icon="reload"
                                            theme="dark"
                                            className={styles.actionBtn}
                                            onClick={() =>
                                                handleResendActivationRequest(
                                                    user.id,
                                                )
                                            }
                                        />
                                    )}
                                    <Button
                                        type="icon"
                                        icon="edit"
                                        theme="dark"
                                        className={styles.actionBtn}
                                        onClick={() => handleEdit(user)}
                                    />
                                    <Button
                                        type="icon"
                                        icon="delete"
                                        theme="dark"
                                        className={styles.actionBtn}
                                        onClick={() =>
                                            handleRequestDelete(user.id)
                                        }
                                    />
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table.Container>
            )}

            {addmodal && (
                <UserModal
                    type="add"
                    onClose={handleCloseModal}
                    onSave={handleSaveModalAdd}
                />
            )}
            {editmodal && (
                <UserModal
                    type="edit"
                    onClose={handleCloseModal}
                    onSave={handleSaveModalEdit}
                    editableUser={editableUser}
                />
            )}
            {deleteModal && (
                <ConfirmActionModal
                    title="Delete this user?"
                    description="Are you sure you wish to delete this user?"
                    handleCancel={closeDeleteModal}
                    theme="light"
                    confirmBtnText={deletePending ? "Deleting user…" : "Delete"}
                    handleConfirm={handleConfirmDeleteUser}
                    pending={deletePending}
                />
            )}
            {resetPasswordId && (
                <ConfirmActionModal
                    title="Reset password?"
                    description="Are you sure you wish to request a password reset for this user?"
                    handleCancel={closeResetPasswordModal}
                    theme="light"
                    confirmBtnText={
                        passwordPending ? "Requesting rest…" : "Request reset"
                    }
                    handleConfirm={handleResetPassword}
                    pending={deletePending}
                />
            )}
            {requestActivationId && (
                <ConfirmActionModal
                    title="Resend activation?"
                    description="Are you sure you wish to resend this user's activation?"
                    handleCancel={closeResendActivationModal}
                    theme="light"
                    confirmBtnText={
                        activationPending
                            ? "Resending activation…"
                            : "Resend activation"
                    }
                    handleConfirm={handleResendActivation}
                    pending={deletePending}
                />
            )}
        </div>
    );
};

export default SettingsPage;
